import { clone } from 'lodash';
import { createLogger } from 'shared/services/logger';
import { addPointerListener } from 'uScope/js/lib/utility/pointerApi';
import us from '../../defines';
import {getImageIdFromSrcAttr, limitValue, divRotate, computeRotationOffset} from '../../lib/utility/helpers';
import {Size, Point} from '../../lib/objects/';
import {env} from '../../lib/utility/browser-compatibility';
import {loadImage} from '../../lib/utility/image-loader';

const logger = createLogger('UScope:extensions:ThumbnailControl');

const defaults = {
  layout: {'divClass': us.clsDIVTHUMBNAIL, 'precinct': 'TR', 'order': 0}
};

export default function ThumbnailControl (uScope, viewport, visibilityPanel, userConfig) {
  var self = this;
  // ..... to group fns in js parser, for ease of search (e.g. search for all 'THUMB')
  // TODO: viewfinder could be packaged into its own object
  var THUMB = this;
  var THUMB_VF = this;

  var isLoaded = false; // thumbnail has been loaded
  var coordSystem_ = us.coordSystemNORM;

  var jVpDiv = viewport.vpDivGet('outer');
  var jThumbDiv;
  var jThumbImg;
  var jVfDiv;

  var csThDims = new Size(0, 0);

  var jHoverDiv;
  var csThDimsPreHover = new Size(0, 0); // pre-hover thumb div size

  var cpVfLT = new Point(0, 0);
  var csVfDims = new Size(0, 0);

  const state = {
    position: cpVfLT,
    size: csVfDims
  };
  self.getState = () => {
    return state;
  };

  var maxWidth_;

  var cpRotOffset = new Point(0, 0); // thumbnail rotation offset
  var csImgDims; //  for quick access,
  var aspectRatio; // aspect ratio of thumbnail

  var navModesImHiddenIn = {}; // what navigation modes not to display the label

  var layout = clone(defaults.layout);
  var reLayout = viewport.overLayout.refreshPrecinct;

  // ............................................ specs
  var thumbSpecs = { // thumbnail settings
    'minWidth': 128,
    'minHeight': 128,
    'resizeInset': 10,
    'scale': 0.20,
    'minScale': 0.1,
    'maxScale': 0.45
  };
  var vfInset = 3; // viewfinder pixel inset from edge of thumbnail

  // ............................................ prefs
  var prefThumbHoverDef = true; // thumbnail hover enlargement preference default

  // ............................................ construct_() local
  var construct_ = function () {
    if (us.ENABLETHUMB == false) return;
    if (viewport.hasAThumbnail() == false) return; // if this image has no thumbnail, return
    csImgDims = viewport.getImageDims(); // compute the viewfinder's dimensions
    us.setAnnotNavModes(navModesImHiddenIn); // hide thumbnail when drawing any annotation

    load_(); // load the thumbnail
  };

  THUMB.events = viewport.events.$new();

  // ............................................ show() public
  THUMB.show = function (show) {
    if (isLoaded == false) return;
    // prevent show if preference is not to show
    if (userConfig.get(us.clsDIVTHUMBNAIL) == false) jThumbDiv.hide();
    else if (show == true) jThumbDiv.show();
    else jThumbDiv.hide();

    reLayout(layout.precinct); // layout just this overlay's precinct
  };
  // ............................................ resize() public
  //    This function This function resizes the thumbnail.
  //    json properties:
  //        auto:     {true, false} indicates if this is an automatic or manual resize event
  //        - a manual resize is called when the user resizes with the thumb's resize handle
  //            or when in multi image and the views are rearranged
  //        - an automatic resize is called when the browser is resized
  THUMB.resize = function (json) {
    if (us.ENABLETHUMB == false || isLoaded == false) {
      return;
    }
    var automatic = json.auto; // false means this resize is not from manual dragging its corner

    var vpDivDims = viewport.vpDivDimsGet();
    var vpWidth = vpDivDims.w;

    maxWidth_ = Math.floor(vpWidth * thumbSpecs.maxScale); // the maximum resize width - it changes with the parent resizing

    jThumbDiv.resizable('option', 'maxWidth', maxWidth_); // enforce the maxWidth dynamically

    // set the thumbnail div's width
    if (automatic == true) { // when auto-resizing, keep current proportion of image/thumbnail
      jThumbDiv.width(Math.floor(vpWidth * thumbSpecs.scale));
    } else if (jThumbDiv.width() > maxWidth_) { // if the thumbnail is sized larger than its maximum allowed - can still happen on browser resize
      jThumbDiv.width(Math.floor(vpWidth * thumbSpecs.maxScale));
    } else if (jThumbDiv.width() < thumbSpecs.minWidth) { // if the the thumbnail is sized smaller than its minimum
      jThumbDiv.width(thumbSpecs.minWidth);
    }

    jThumbDiv.height(Math.floor(jThumbDiv.width() * aspectRatio)); // set the thumbnail div's height and update its scale
    thumbSpecs.scale = (jThumbDiv.width() / vpWidth).toFixed(2);

    csThDims.set(jThumbDiv.width(), jThumbDiv.height()); // and save the updated thumb dims for quick access
    jThumbImg.width(csThDims.w).height(csThDims.h); // and set the thumb image dims

    // note: upon rotation @ the div center the div's offset is its LT corner shift
    var angleRad = viewport.getViewAngle(); // update the angle

    refreshLayout_(csThDims.w, csThDims.h, angleRad);

    self.viewFinderRefresh(); // update the viewfinder position

    // TODO: Is the Viewport event sufficient to handle this or do we need this?
    THUMB.events.trigger('thumbnail:resize', json);
  };

  // ............................................ rotate() public
  //    This function rotates the thumbnail and refreshes its position in the view
  //    to account for the boudning box size difference due to rotation.
  let lastAngle;
  THUMB.rotate = function ({angleRad}) {
    if (!us.ENABLETHUMB || !isLoaded || angleRad === lastAngle) {
      return;
    }
    const thW = csThDims.w;
    const thH = csThDims.h;

    divRotate(jThumbDiv[0], 0, 0, thW, thH, angleRad);
    cpRotOffset = refreshLayout_(thW, thH, angleRad);
    lastAngle = angleRad;
  };

  // ............................................ viewFinderMove() public
  //    This function positions the thumbnail's viewfinder to refect the viewport's window into the image.
  //
  //    parameters:
  //        action:            the caller id used for reporting purposes

  THUMB_VF.viewFinderMove = function (action) {
    if (isLoaded == false) return;

    var cpViewLT = viewport.getViewLeftTop(coordSystem_); // compute the viewfinder's left top position
    var left = Math.floor(csThDims.w * cpViewLT.x / csImgDims.w);
    var top = Math.floor(csThDims.h * cpViewLT.y / csImgDims.h);
    if (cpVfLT.x == left && cpVfLT.y == top) { // don't do anything if the coords have not changed
      return;
    }

    if (cpVfLT.x != left || cpVfLT.y != top) { // don't do anything if the coords have not changed
      cpVfLT.x = left;
      cpVfLT.y = top;
      viewFinderDraw(action);
    }
  };

  // ............................................ viewFinderRefresh() public
  //    This function positions and sizes the thumbnail's viewfinder to refect the viewport's window into the image.
  //
  //    parameters:
  //        action:        the caller id used for reporting purposes

  THUMB_VF.viewFinderRefresh = function () {
    if (isLoaded == false) {
      return;
    }

    viewFinderDimsUpdate_(); // update the viewfinder's width and height
    // compute the viewfinder's left top position
    var cpViewLT = viewport.getViewLeftTop(coordSystem_);
    cpVfLT.x = Math.floor(csThDims.w * cpViewLT.x / csImgDims.w);
    cpVfLT.y = Math.floor(csThDims.h * cpViewLT.y / csImgDims.h);

    viewFinderDraw();

    // ............................................
    // closure functions
    // ............................................ viewFinderDimsUpdate_
    function viewFinderDimsUpdate_ () {
      var vfW = Math.floor(csThDims.w * viewport.getViewWidth() / csImgDims.w);
      var vfH = Math.floor(csThDims.h * viewport.getViewHeight() / csImgDims.h);
      csVfDims.set(vfW, vfH);
    }
    // ............................................
  };
  // ............................................ viewFinderDraw() local
  //    This function insures the view finder rect is within the thumbnail limits, draws it
  var viewFinderDraw = function () {
    var left = cpVfLT.x;
    var top = cpVfLT.y;
    var width = csVfDims.w;
    var height = csVfDims.h;

    if (left < vfInset) { // constrain the viewfinder inside the thumbnail
      width = width + left - vfInset - 1;
      left = vfInset;
    }

    if (top < vfInset) {
      height = height + top - vfInset - 1;
      top = vfInset;
    }

    width = limitValue(width, vfInset, csThDims.w - vfInset - left - 1);
    height = limitValue(height, vfInset, csThDims.h - vfInset - top - 1);

    if (env.isIE) { // update the viewfinder div's position and size
      jVfDiv.css({
        'left': (left - 4) + 'px',
        'top': (top - 4) + 'px',
        'width': (width + 4) + 'px',
        'height': (height + 4) + 'px'
      });
    } else {
      jVfDiv.css({
        'left': left + 'px',
        'top': top + 'px',
        'width': width + 'px',
        'height': height + 'px'
      });
    }
  };

    // ............................................ getThumbScale() local
  THUMB.getThumbScale = function () {
    var scaleX = csThDims.w / csImgDims.w;
    return scaleX;
  };
  // ............................................ dimensionsGet() local
  THUMB.dimensionsGet = function () {
    return new Size(csThDims.w, csThDims.h);
  };

  // ............................................ load_() local
  //    This function creates the thumbnail image div and loads it with the thumbnail.
  //    When the image loaded it calls into the layout manager to position the thumbnail according
  //    to its layout settings and adds the thumbnail viewfinder element
  var load_ = function () {
    if (jThumbDiv) return; // if this viewport already has a thumbnail, return

    // first create an offscreen div that will contain the Thumbnail img and add it to the vp
    jThumbDiv = $('<div/>', { // build the div tag that contains the img tag
      'class': us.clsDIVTHUMBNAIL + ' ' + us.clsOVERLAY, // overlay class sets the z-index
      css: {'left': us.kDEF_TILE_POS_PX, 'top': us.kDEF_TILE_POS_PX, 'overflow': 'hidden'}
    });

    // Expose the container for thumbnail overlay access (such as the heatmap)
    THUMB.$container = jThumbDiv;

    if (us.ENABLETHUMBHOVER) {
      // build the div tag that evokes the hover to enlarge the thumbnail action
      jHoverDiv = $('<div/>', {'class': us.clsDIVTHUMBHOVER + ' ' + us.clsSUPEROVERLAY});
      jThumbDiv.append(jHoverDiv);
    }

    jVpDiv.append(jThumbDiv); // place the thumb div in its viewport
    jThumbDiv = jVpDiv.find('.' + us.clsDIVTHUMBNAIL);

    // next build the image tag and load the thumbnail image
    thumbSpecs.scale = (env.isIOS ? 0.3 : thumbSpecs.scale); // IOS requires a larger thumbnail
    var width = Math.floor(jVpDiv.width() * 0.5); // the requested thumbnail width

    var sThumbImgSrc = viewport.urlBuilder.thumb(width, 0);

    loadImage(sThumbImgSrc, {
      className: us.clsIMGTHUMBNAIL,
      alt: ''
    })
      .then((image) => {
        // check that this viewport's imageId === this image source's imageId
        var srcId = getImageIdFromSrcAttr(image); // format "@2"
        if (viewport.getImageId() !== srcId) { // if they don't match, do not load this thumbnaail (async return from another image!)
          logger.warn('...thumbnail:load: vpDiv id=' + viewport.getImageId() + '  srcId=' + srcId + ' - skipping load!!');
          return;
        }
        if (isLoaded) { // don't load the same thumbnail multiple times (happens when VIL reopens same image)
          logger.warn('...thumbnail:load: Thumbnail image for (' + viewport.getImageId() + ') received again!!');
          return;
        }
        isLoaded = true;

        // now the requested thumbnail is larger than the initial div it is displayed in so the div and the image must be resized
        //    - this is done so that when thumbnail is resized it isn't pixelated
        var csThumbnail = viewport.getThumbFileDims(); // get the initial width of the thumbnail div

        var vpDivDims = viewport.vpDivDimsGet();
        var vpW = vpDivDims.w;
        var vpH = vpDivDims.h;

        var thW = Math.floor(vpW * thumbSpecs.scale);
        var thH = Math.floor(thW * csThumbnail.h / csThumbnail.w);

        if (thH / vpH > thumbSpecs.maxScale) { // if this width-based scaling makes the th height too big
          thH = Math.floor(vpH * thumbSpecs.maxScale);
          thW = Math.floor(thH * csThumbnail.w / csThumbnail.h);
          thumbSpecs.scale = (thW / vpW).toFixed(2);
        } else if (thH / vpH < thumbSpecs.minScale) { // if this width-based scaling makes the th height too small
          thumbSpecs.scale = thumbSpecs.maxScale;
          thW = Math.floor(vpW * thumbSpecs.scale);
          thH = Math.floor(thW * csThumbnail.h / csThumbnail.w);
        }
        aspectRatio = thH / thW; // save the aspect ratio of thumbnail
        jThumbDiv.width(thW).height(thH);
        csThDims.set(thW, thH); // save updated thumb dims for quick access
        csThDimsPreHover.setSize(csThDims); // save pre-hover size of thumbnail

        jThumbDiv.append(image); // place the thumbnailImg in the DOM's thumb div in the viewport
        jThumbImg = jThumbDiv.find('.' + us.clsIMGTHUMBNAIL);
        jThumbImg.width(thW).height(thH); // now set the thumbnail image dimensions to that of its div

        viewport.overLayout.add(layout); // and call into layout with parms to place the div

        viewFinderLoad_(); // and add the thumbnail's viewfinder rect

        // TODO: Trigger load and just do what you need to there.
        self.resize({auto: true}); // resize to set the thumbnail size
        self.show(true); // show/hide according to user preferences

        THUMB.events.trigger('thumbnail:thumbnail-loaded');
      })
      .catch(function (e) {
        logger.error('thumbnail: load: error');
      });

    bindEvents_(); // add event handlers

    // ..................................................
    // closure functions
    // ............................................ bindEvents_()
    // bindEvents_ - This function adds the thumbnail event handlers.
    //
    //    Note that by using the same namespace as the viewport when the
    //    viewport removes its events by namespace, these are also removed.
    function bindEvents_ () {
      var namespace = viewport.eventNameSpaceGet();

      // show/hide thumbnail on mode change
      $(us.idHashROOT).on(us.evMODECHANGE + namespace, function (e, parms) {
        // show is true if this is the image selected and not in a mode that I'm supposed to hide during
        var bshow = !!(viewport.getImageIdx() == uScope.getSelImageIdx() && navModesImHiddenIn[parms.navMode] == undefined);
        self.show(bshow);
        // self.viewFinderRefresh();
      });

      // show/hide thumbnail if image is a control sample
      $(us.idHashROOT).on(us.evIMAGESELECTED + namespace, function (e, parms) {
        self.show(parms.uScopeIdx == viewport.getImageIdx());
      });

      // ............................................ user preference update
      $(us.idHashROOT).on(us.evUSERPREFSREADY + namespace, function (e, parms) {
        self.show(userConfig.get(us.clsDIVTHUMBNAIL));
      });

      $(us.idHashROOT).on(us.evUSERPREFSET + namespace, function (e, parms) { // parms: {className: STR, value: bool}
        if (parms.className == us.clsDIVTHUMBNAIL) {
          self.show(parms.value);
        } else if (parms.className == us.CVSHEATMAP && parms.value == true && userConfig.get(us.clsDIVTHUMBNAIL) == false) {
          visibilityPanel.check(us.clsDIVTHUMBNAIL, true);
        }
      });

      $(us.idHashROOT).on(us.evUSERPREFDEFAULTTHUMBHOVER + namespace, function (e, parms) {
        userConfig.set(us.clsDIVTHUMBHOVER, prefThumbHoverDef);
      });

      // ............................................ hover over thumb action
      if (us.ENABLETHUMBHOVER) {
        // ..................................................
        // closure functions
        var hoverThumbTimerRemove_ = function () {
          var timer = jThumbDiv.data('hover');
          if (timer) {
            clearTimeout(timer); // stop the timer
            jThumbDiv.data('hover', null); // remove the  timer in the thumb div data
          }
        };
        var resizeTo = function (width) {
          jThumbDiv.width(width);
          self.resize({auto: false});
        };
        // ..................................................
        // hover event related
        jHoverDiv.hover( // enlarge the thumbnail on hover
          function (e) {
            if (!userConfig.get(us.clsDIVTHUMBHOVER)) {
              return;
            }

            var timer = jThumbDiv.data('hover'); // show the panel when the mouse hovers over the toolbar icon
            var hoverOn = jThumbDiv.data('hoverOn');

            if (!hoverOn && !timer && csThDims.w < maxWidth_ * 0.75) { // if no timer set and the thumbnail size is not already large
              timer = setTimeout(function () { // then set timer that will fire the hover action after 2 seconds
                csThDimsPreHover.setSize(csThDims); // save pre-hover size of thumbnail
                resizeTo(csThDims.w * 1.5); // enlarge the thumbnail
                jThumbDiv.data('hoverOn', true); // flag that hover enlargement is on
                hoverThumbTimerRemove_(); // and remove the timer
              }, 150); // delay time in milli-sec (this actually takes a lot longer)
              jThumbDiv.data('hover', timer); // save timer in the thumb div data
            }
          },
          function () { // handler out
            hoverThumbTimerRemove_();
          }
        );
        jThumbDiv.on('mouseleave' + namespace, function () { // return the thumbnail size back to its pre-hover size
          if (userConfig.get(us.clsDIVTHUMBHOVER) == false) { // check if pref enables hover action
            return;
          }

          resizeTo(csThDimsPreHover.w); // return the thumbnail to its pre-hover size
          jThumbDiv.data('hoverOn', false); // flag that hover enlargement is off
          hoverThumbTimerRemove_(); // remove the timer
          return false;
        });
      }

      // ............................................ resize, click, mouse, ...
      if (layout.precinct == 'BR') {
        jThumbDiv.resizable({
          cursor: 'move',
          handles: 'nw',
          aspectRatio: true,
          distance: 3,
          minWidth: thumbSpecs.minWidth,
          start: function (e, ui) {
            e.preventDefault();
            return false;
          },
          resize: function (e, ui) {
            self.resize({auto: false});
            csThDimsPreHover.setSize(csThDims); // save pre-hover size of thumbnail
            jThumbDiv.data('hoverOn', false); // flag that hover enlargement is off
            e.preventDefault();
            return false;
          },
          stop: function (e, ui) {
            e.preventDefault();
            return false;
          }
        });
      } else {
        jThumbDiv.resizable({
          cursor: 'move',
          handles: 'sw',
          aspectRatio: true,
          distance: 3,
          minWidth: thumbSpecs.minWidth,
          start: function (e, ui) {
            e.preventDefault();
            return false;
          },
          resize: function (e, ui) {
            self.resize({auto: false});
            csThDimsPreHover.setSize(csThDims); // save pre-hover size of thumbnail
            jThumbDiv.data('hoverOn', false); // flag that hover enlargement is off

            e.preventDefault();
            return false;
          },
          stop: function (e, ui) {
            e.preventDefault();
            return false;
          }
        });
      }

      const thumbDiv = jThumbDiv[0];
      addPointerListener(thumbDiv, 'mousedown', function (e) {
        viewport.setImageSelected(); // inform uScope that this image is in focus

        // ios tap never sends thumb up to switch out of thumb drag mode //cp-test if this is still necessary
        if (viewport.navModeGet() == us.eNavModes.nmThumbDragImage) { viewport.navModeHalt(); }
        // if no current navigation is happening then move the image via click on thumbnail
        //    - move on viewport onto thumbnail prevent odd behavior
        if (viewport.navModeGet() == us.eNavModes.nmNone) {
          var cpThPos = withinInset_(e);
          if (cpThPos != false) { // if not on the very edge of the thumbnail
            viewport.navMode = us.eNavModes.nmThumbDragImage; // anticipate thumb drag mode
            navEvent_(e); // move the view
          }
        } else {
          logger.warn('... th_mousedown: ignored - nav mode != none');
        }
        return false; // prevent propigation to VP
      });

      addPointerListener(thumbDiv, 'mousemove', function (e) {
        if (viewport.navModeGet() == us.eNavModes.nmThumbDragImage) { // if dragging viewfinder around the thumbnail
          var cpThPos = withinInset_(e);
          if (cpThPos != false) { // if not on the very edge of the thumbnail
            navEvent_(e); // move the view
          }
        }
        return true; // propagate necessary or thumb resize does not reduce in size
      });

      // stops drag in viewport from continuing
      addPointerListener(thumbDiv, 'focus', stopDrag);
      addPointerListener(thumbDiv, 'mouseup', stopDrag);
      function stopDrag (e) { // stops drag in viewport from continuing
        // note that almost always the thumbnail mouse up event is caught in vp_mouseup and handled there
        viewport.navModeHalt();
        return true; // Propagate necessary or thumb resize does not release
      }

      jThumbDiv.on('dblclick' + namespace, function (e) {
        return false; // prevent dblclick on thumbnail causing double click zoom on viewport
      });

      viewport.events.on('viewport:resize', (auto) => {
        self.resize({auto});
      });

      viewport.renderer.addLayer({
        paint: self.viewFinderRefresh.bind(self),
        rotateChange: self.rotate
      });
    }
    // ............................................
    // closure functions
    // ............................................ navEvent_
    //    This function This function moves the view to the image position indicated by the event on the thumbnail.
    function navEvent_ (e) {
      var cpThPos = coordPageToThumb_(e); // mouse position wrt top/left of thumbnail
      var cpBaseCoord = coordBaseImage_(cpThPos); // get this position in base coords

      viewport.moveAbsolute(cpBaseCoord.x, cpBaseCoord.y, coordSystem_);
      viewport.setMouseCur(cpBaseCoord.x, cpBaseCoord.y);

      self.viewFinderMove('navevent'); // position the thumbnail viewfinder
    }
    // ............................................ withinInset_
    //    This function determines if an event's page coords are within the thumbnail by the specified inset (margin).
    //    Passed Parameters: event
    //    Returns a Point of the cursor position within the thumbnail or false if cursor position isn't within the inset
    function withinInset_ (e) {
      var cpThPos = coordPageToThumb_(e); // get cursor position inside this element

      var inset = thumbSpecs.resizeInset || 10;
      if (cpThPos.x < inset || cpThPos.y < inset) {
        return false;
      }

      if (cpThPos.x > (jThumbDiv.width() - inset) || cpThPos.y > (jThumbDiv.height() - inset)) {
        return false;
      }
      return cpThPos;
    }
    // ............................................
  };
    // ............................................ viewFinderLoad_() local
    //    This function This function creates the div for the thumbnail's viewfinder and appends
    //    it to the thumbnail div then calls for the viewfinder to be positioned to indicate
    //    the viewport's window into the image.
  var viewFinderLoad_ = function () {
    if (isLoaded == false) return;

    var vfW = Math.floor(jThumbDiv.attr('offsetWidth') / 4); // default view finder dimensions
    var vfH = Math.floor(jThumbDiv.attr('offsetHeight') / 4); //  - to be replaced when vf is positioned

    jVfDiv = $('<div/>', { // build the div tag that contains the viewfinder
      'class': us.clsDIVVIEWFINDER + ' ' + us.clsSUPEROVERLAY,
      css: {
        'left': '0px',
        'top': '0px',
        'width': vfW,
        'height': vfH,
        'overflow': 'auto', // necessary for IE
        'border-color': (viewport.getCaptureType() == us.eAICCaptureType.aic_i_FL) ? '#FFFFFF' : '#000000',
        'outline-color': (viewport.getCaptureType() == us.eAICCaptureType.aic_i_FL) ? '#000000' : '#FFFFFF'
      }
    });
    if (env.isIE) jVfDiv.css({'outline-width': '0'});

    jThumbDiv.append(jVfDiv); // and add it to the thumbnail div
    jVfDiv = jVpDiv.find('.' + us.clsDIVVIEWFINDER);

    // Probably not needed
    // self.viewFinderRefresh();                             // now position the view finder to indicate the viewport
  };

    // ............................................ refreshLayout_() local
    //    This function computes the thumbnail's rotation offset and refreshes its layout.
    //    note: upon rotation @ the div center the div's offset is its LT corner shift
  var refreshLayout_ = function (thW, thH, angleRad) {
    cpRotOffset = computeRotationOffset(thW, thH, angleRad); // compute and save the rotation offset in member var

    layout.rightOffset = -cpRotOffset.x; // update the thumbnail layout info for the the rotation offset
    layout.bottomOffset = -cpRotOffset.y; // bc layout obj holds a ref to the var layout, its refresh picks up the change

    reLayout(layout.precinct); // layout just this overlay's precinct
    return cpRotOffset;
  };

    // ............................................ coordPageToThumb_() local
    //    This function determines the cursor position relative to (within) the thumbnail
    //                        as if it was not rotated.
    //    Passed Parameters:
    //                event object
    //    Returns:
    //                a Point of the cursor position within the unrotated thumbnail
  var coordPageToThumb_ = function (e) {
    let cpRtn;

    var tgtOff = jThumbDiv.offset(); // the current position of the target relative to the document
    var thX = e.pageX - tgtOff.left; // translate thumbnail left top position
    var thY = e.pageY - tgtOff.top;
    cpRtn = coordUnRotate_(thX, thY); // un-rotate the thumbnail coordinate

    cpRtn.x = limitValue(cpRtn.x, 0, csThDims.w - 1); // insure the coords are within limits
    cpRtn.y = limitValue(cpRtn.y, 0, csThDims.h - 1);
    return cpRtn;
  };
    // ............................................ coordUnRotate_() local
    //    This function converts rotated thumbnail coords to un-rotated thumb coords.
    //    Passed Parameters:
    //        thX, thY:    [rotated] thumbnail relative x,y pixel position
    //    Returns:
    //        Point object with un-rotated thumbnail relative x,y pixel position

  var coordUnRotate_ = function (thX, thY) {
    var cpRtn = new Point(thX, thY); // return position object
    var imgRotAngleRad = viewport.getViewAngle();
    if (imgRotAngleRad != 0) {
      var ptx = thX + cpRotOffset.x;
      var pty = thY + cpRotOffset.y;
      var xc = csThDims.w / 2;
      var yc = csThDims.h / 2; // Thumbnail center position

      var dx = ptx - xc;
      var dy = yc - pty; // Transform Point Coordinates to Thumbnail center (xc,yc)
      var hyp = Math.sqrt(dx * dx + dy * dy); //    Hypotenuse
      var thetaRad = Math.atan2(dy, dx); // Transformed Point Angle

      var phiRad = thetaRad + imgRotAngleRad; // Add Rotation to Point Angle
      cpRtn.x = hyp * Math.cos(phiRad); // Compute Shift
      cpRtn.y = hyp * Math.sin(phiRad);

      var xi = cpRtn.x + xc;
      var yi = yc - cpRtn.y; // compute x-shift and convert to image coordiantes
      cpRtn.x = Math.round(xi);
      cpRtn.y = Math.round(yi);
    }
    return cpRtn;
  };
    // ............................................ coordBaseImage_() local
    //    This function returns the base image coordinate corresponding to a pixel position on the thumbnail.
    //    input unrotated thumbnail click point (must be inside thumbnail) returns a Point of .....
  var coordBaseImage_ = function (cpThumb) {
    var cpRtn = new Point(-1, -1); // return position object

    var imageWidth = viewport.getImageWidth();
    var viewZoom = viewport.getViewZoom();

    var vpDivDims = viewport.vpDivDimsGet();
    var vpWidth = vpDivDims.w;
    var vpHeight = vpDivDims.h;

    var thumbZoom = imageWidth / csThDims.w;
    // compute the mouse position in base image coords
    cpRtn.x = Math.floor(thumbZoom * cpThumb.x - vpWidth * viewZoom / 2);
    cpRtn.y = Math.floor(thumbZoom * cpThumb.y - vpHeight * viewZoom / 2);
    return cpRtn; // and return it
  };

  construct_();
}



// WEBPACK FOOTER //
// ./src/uScope/js/extensions/thumbnail/thumbnail-control.js